import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { Button } from "./components/ui";
import { useLocale, useSyncLocaleFromUser, useT } from "./i18n";
import { isUpdateAvailable, onUpdateAvailable } from "./lib/api";
import { authClient } from "./lib/auth";
import { MeProvider, useMe } from "./lib/me";
import { ProjectsProvider } from "./lib/projects";
import { DashboardPage } from "./pages/DashboardPage";
import { InvitePage } from "./pages/InvitePage";
import { LoginPage } from "./pages/LoginPage";
import { ProjectPage } from "./pages/ProjectPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { TourPage } from "./pages/TourPage";
import { SettingsPage } from "./pages/settings/SettingsPage";

// Lazy: zieht three.js/Photo Sphere Viewer nur, wenn der Rundgang-Editor
// wirklich geöffnet wird (eigener Chunk statt im Haupt-Bundle).
const HotspotEditorPage = lazy(() =>
  import("./pages/HotspotEditorPage").then((m) => ({ default: m.HotspotEditorPage })),
);

/** Deep-Links auf die frühere Statistik-Seite landen im Statistik-Tab. */
function StatsRedirect() {
  const { id = "" } = useParams();
  return <Navigate to={`/tours/${id}?tab=stats`} replace />;
}

export function App() {
  const { data: session, isPending } = authClient.useSession();
  // better-auth lädt die Session nach jedem Auth-Aufruf neu (sign-in, 2FA-Code
  // senden …) und meldet dabei isPending, solange keine Session existiert. Der
  // Lade-Screen darf deshalb nur den allerersten Abruf abdecken — sonst wird
  // die LoginPage bei jedem Neuladen ausgehängt und verliert ihren Zustand
  // (der 2FA-Code-Schritt käme nie auf den Schirm).
  const [sessionResolved, setSessionResolved] = useState(false);
  if (!isPending && !sessionResolved) setSessionResolved(true);
  const location = useLocation();
  const t = useT();
  const [locale] = useLocale();
  const syncLocale = useSyncLocaleFromUser();

  // Gespeicherte Sprache des Users übernehmen, sobald die Session da ist
  const userLocale = (session?.user as { locale?: string } | undefined)?.locale;
  useEffect(() => {
    if ((userLocale === "de" || userLocale === "en") && userLocale !== locale) {
      syncLocale(userLocale);
    }
  }, [userLocale, locale, syncLocale]);

  if (!sessionResolved) {
    return (
      <div className="grid min-h-dvh place-items-center text-sm text-zinc-400">
        {t.common.loading}
      </div>
    );
  }

  if (!session) {
    // Reset-Link aus der „Passwort vergessen“-Mail und Einladungslinks funktionieren ohne Session
    if (location.pathname.startsWith("/reset-password")) return <ResetPasswordPage />;
    if (location.pathname.startsWith("/invite/")) return <InvitePage session={null} />;
    return <LoginPage />;
  }

  return (
    <MeProvider userId={session.user.id}>
      <Shell name={session.user.name} email={session.user.email} userId={session.user.id} />
    </MeProvider>
  );
}

/** Eingeloggter Teil: wartet auf /api/me (aktiver Account, Rollen) und baut Rahmen + Routen. */
function Shell({ name, email, userId }: { name: string; email: string; userId: string }) {
  const { me, error } = useMe();
  const [updateReady, setUpdateReady] = useState(isUpdateAvailable());
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => onUpdateAvailable(() => setUpdateReady(true)), []);

  const signOut = () =>
    // Zurück auf / — sonst zeigt der Login die alte Route und der
    // nächste Login landet mitten in einer Tour-Seite
    void authClient.signOut().then(() => navigate("/", { replace: true }));

  if (error) {
    return (
      <div className="grid min-h-dvh place-items-center text-sm text-red-600 dark:text-red-400">
        {t.common.error}
      </div>
    );
  }
  if (!me) {
    return (
      <div className="grid min-h-dvh place-items-center text-sm text-zinc-400">
        {t.common.loading}
      </div>
    );
  }

  const account = me.activeAccount;
  const accountRole = account?.role ?? null;

  // Einladung im eingeloggten Zustand: eigene Seite ohne App-Rahmen
  if (location.pathname.startsWith("/invite/")) return <InvitePage session={{ email }} />;

  return (
    <ProjectsProvider enabled={Boolean(account)}>
      <div className="min-h-dvh">
        {updateReady && (
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-accent py-2 text-center text-sm font-medium text-white"
          >
            {t.app.updateBanner}
          </button>
        )}
        <Routes>
          {/* Rundgang-Editor: eigener Vollbildmodus, bewusst außerhalb des Rahmens */}
          <Route
            path="/tours/:id/editor"
            element={
              <Suspense
                fallback={
                  <div className="grid h-dvh place-items-center bg-zinc-950 text-sm text-zinc-400">
                    {t.common.loading}
                  </div>
                }
              >
                <HotspotEditorPage />
              </Suspense>
            }
          />
          <Route
            element={
              <AppShell
                name={name}
                email={email}
                account={account}
                accounts={me.accounts}
                canCreateAccount={me.isOperator}
                onSignOut={signOut}
              />
            }
          >
            {account ? (
              <>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/projects/:id" element={<ProjectPage />} />
                <Route path="/tours/:id" element={<TourPage />} />
                {/* Alter Statistik-Link: die Statistik ist jetzt ein Tab der Tour-Seite */}
                <Route path="/tours/:id/stats" element={<StatsRedirect />} />
              </>
            ) : (
              <Route
                path="/"
                element={<NoAccount onSignOut={signOut} isOperator={me.isOperator} />}
              />
            )}
            <Route
              path="/settings/*"
              element={
                <SettingsPage
                  accountRole={accountRole}
                  accountName={account?.name ?? null}
                  myUserId={userId}
                />
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </div>
    </ProjectsProvider>
  );
}

/** User ohne Mitgliedschaft (z. B. frisch aus einem Account entfernt); Betreiber legen oben einen an. */
function NoAccount({ onSignOut, isOperator }: { onSignOut: () => void; isOperator: boolean }) {
  const t = useT();
  return (
    <main className="mx-auto w-full max-w-md px-4 py-16 text-center">
      <h2 className="text-lg font-semibold">{t.app.noAccountTitle}</h2>
      <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
        {isOperator ? t.app.noAccountOperatorText : t.app.noAccountText}
      </p>
      <Button variant="ghost" className="mt-6" onClick={onSignOut}>
        {t.menu.signOut}
      </Button>
    </main>
  );
}
